// Funnel v2 thanks page — state-aware close of the purchase flow.
//
//   ?consent=1 → the buyer took the free upgrade: card saved, trial armed.
//   otherwise  → Blueprint-only path, treated as a complete, respectable
//                choice (decliners converting later is the primary metric).
//
// No time-count framing for the Blueprint anywhere on this page.
//
// Issue #978: "Open your Blueprint" used to link straight to /login, landing
// a 60-second-old buyer on a generic sign-in wall (and, before #977 was
// fixed, with no email even sent yet). When ?payment_intent_id is present we
// now try to claim a same-device session via /api/claim-session — success
// sends the CTA straight to /auth/callback?token_hash=... (AuthCallbackPage's
// existing verifyOtp handling), no email round-trip on this device. If the
// claim isn't ready (webhook still in flight, or no payment_intent_id at all
// — e.g. a bookmarked/shared link), the CTA falls back to /login with the
// email prefilled and post-purchase framing (see LoginPage's `from=purchase`
// handling) rather than the plain waitlist-adjacent login wall.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BadgeCheck, BookOpen, Mail, Route } from 'lucide-react';
import { Kicker, PrimaryCtaLink } from '../../components/marketing/funnel/atoms';
import { claimSession } from '../../lib/funnel-api';

const CLAIM_RETRY_DELAYS_MS = [800, 1600, 2400];

export function ThanksPage() {
  const [params] = useSearchParams();
  const consent = params.get('consent') === '1';
  const paymentIntentId = params.get('payment_intent_id');

  const [claimedTokenHash, setClaimedTokenHash] = useState<string | null>(null);
  const [fallbackEmail, setFallbackEmail] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!paymentIntentId) return;

    async function attemptClaim() {
      for (const delayMs of [0, ...CLAIM_RETRY_DELAYS_MS]) {
        if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));
        if (cancelledRef.current) return;

        const result = await claimSession(paymentIntentId as string);
        if (result.status === 'ready') {
          if (!cancelledRef.current) setClaimedTokenHash(result.tokenHash);
          return;
        }
        if (result.status === 'pending' && result.email && !cancelledRef.current) {
          setFallbackEmail(result.email);
        }
      }
    }

    void attemptClaim();
    return () => {
      cancelledRef.current = true;
    };
  }, [paymentIntentId]);

  const ctaHref = claimedTokenHash
    ? `/auth/callback?token_hash=${encodeURIComponent(claimedTokenHash)}&type=magiclink`
    : `/login?from=purchase${fallbackEmail ? `&email=${encodeURIComponent(fallbackEmail)}` : ''}`;

  return (
    <section className="px-5 py-14 md:px-8 md:py-24">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <div className="fv2-reveal mx-auto flex h-16 w-16 items-center justify-center rounded-lg border border-brand-teal/25 bg-brand-teal/10">
            <BadgeCheck className="h-8 w-8 text-brand-teal" />
          </div>
          <Kicker className="fv2-reveal mt-6 !text-brand-teal/90" >
            {consent ? 'Card saved · nothing charged today' : 'Order confirmed'}
          </Kicker>
          <h1
            style={{ '--fv2-i': 1 } as React.CSSProperties}
            className="fv2-reveal mt-4 font-display text-4xl font-semibold leading-[1.04] tracking-tight text-white md:text-6xl"
          >
            {consent ? 'Your Blueprint is ready. Your trial is set up and waiting.' : 'Your Blueprint is ready.'}
          </h1>
          <p
            style={{ '--fv2-i': 2 } as React.CSSProperties}
            className="fv2-reveal mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300"
          >
            {consent
              ? 'When your plan is complete, one click deploys it in VibeControl — your 14-day trial and 2,500 credits of agent work start at that moment, not before.'
              : 'Start the guided conversation whenever you are ready, and come back as often as it takes to build the clearest plan for your business.'}
          </p>
        </div>

        <div
          style={{ '--fv2-i': 3 } as React.CSSProperties}
          className="fv2-reveal mt-12 grid gap-4 text-left md:grid-cols-3"
        >
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
            <Mail className="mb-4 h-5 w-5 text-brand-coral/90" />
            <h2 className="font-semibold text-white">1 · Check your inbox</h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Your access email is on its way. It signs you straight into your Blueprint.
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
            <Route className="mb-4 h-5 w-5 text-brand-teal" />
            <h2 className="font-semibold text-white">2 · Start the conversation</h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              The more you give it, the more specific your plan comes out. Sessions save as you
              go.
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
            <BookOpen className="mb-4 h-5 w-5 text-brand-teal" />
            <h2 className="font-semibold text-white">
              {consent ? '3 · Everything is attached' : '3 · Implement your way'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              {consent
                ? 'The implementation course, your trial, and the 2,500-credit bundle are tied to this Blueprint and waiting for you to activate.'
                : 'Your written plan works with the AI tools you already use. VibeControl stays available inside the app whenever you want the one-click path.'}
            </p>
          </div>
        </div>

        <div style={{ '--fv2-i': 4 } as React.CSSProperties} className="fv2-reveal mt-12 text-center">
          <PrimaryCtaLink to={ctaHref}>Open your Blueprint</PrimaryCtaLink>
          <p className="mt-5 text-sm text-slate-500">
            The access email works on any device. Keep it.
          </p>
        </div>
      </div>
    </section>
  );
}
